import { useSyncExternalStore } from 'react';
import {
  tShared, getSharedComponentLang, subscribeSharedComponentLang,
  type SharedI18nKey,
} from '../../lib/shared-i18n';
import './StatusBadge.css';

/**
 * StatusBadge — 시안 ApprovalView (line 3352~) 의 statusMeta 6색 + backend
 * approval enum (DRAFT/IN_PROGRESS/APPROVED/REJECTED/RECALLED) 합집합.
 *
 * pending_me / approved_me / shared 는 시안 전용 frontend 분기 (backend 없음).
 * backend status 직매칭이 우선이고, 시안 분기는 page 안에서 별도 결정 후 status prop 으로 전달.
 *
 * 색은 tokens.css 변수 매핑 (--c-success / --c-primary / --c-danger / --c-warning / --c-violet).
 *
 * Cycle 71 — i18n 적용 (shared-i18n.ts 신규 모듈). frontend 6 enum + backend 5 enum 의
 * 중복 (결재완료/결재중/반려) 은 같은 key (shared.status.{approved/in_progress/rejected}) 로 SSOT.
 */

export type ApprovalStatus =
  // 시안 enum (frontend)
  | 'approved'
  | 'in_progress'
  | 'rejected'
  | 'pending_me'
  | 'approved_me'
  | 'shared'
  // backend enum
  | 'DRAFT'
  | 'IN_PROGRESS'
  | 'APPROVED'
  | 'REJECTED'
  | 'RECALLED';

interface StatusMeta {
  labelKey: SharedI18nKey;
  modifier: string; // CSS class 접미사
}

const STATUS_META: Record<ApprovalStatus, StatusMeta> = {
  // 시안 enum
  approved:    { labelKey: 'shared.status.approved',    modifier: 'success' },
  in_progress: { labelKey: 'shared.status.in_progress', modifier: 'primary' },
  rejected:    { labelKey: 'shared.status.rejected',    modifier: 'danger' },
  pending_me:  { labelKey: 'shared.status.pending_me',  modifier: 'warning' },
  approved_me: { labelKey: 'shared.status.approved_me', modifier: 'success' },
  shared:      { labelKey: 'shared.status.shared',      modifier: 'violet' },
  // backend enum (중복 라벨 — SSOT 같은 key)
  DRAFT:       { labelKey: 'shared.status.draft',       modifier: 'neutral' },
  IN_PROGRESS: { labelKey: 'shared.status.in_progress', modifier: 'primary' },
  APPROVED:    { labelKey: 'shared.status.approved',    modifier: 'success' },
  REJECTED:    { labelKey: 'shared.status.rejected',    modifier: 'danger' },
  RECALLED:    { labelKey: 'shared.status.recalled',    modifier: 'neutral' },
};

interface StatusBadgeProps {
  status: ApprovalStatus | string; // unknown 은 fallback
}

export function StatusBadge({ status }: StatusBadgeProps) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const meta = STATUS_META[status as ApprovalStatus];
  if (meta) {
    return (
      <span className={`status-badge status-badge--${meta.modifier}`}>
        {tShared(meta.labelKey)}
      </span>
    );
  }
  // unknown fallback — raw status 표시 (i18n 미적용 — backend 신규 enum 미등록 안전망)
  return (
    <span className="status-badge status-badge--neutral">
      {String(status)}
    </span>
  );
}
